import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react'

import type { IContentSpaceSession } from '../types'
import { IScormSessionSnapshot, ScormSession } from './ScormSession'

const ScormSessionContext = createContext<ScormSession | null>(null)

interface IProviderProps {
  initialSession: IContentSpaceSession
}

// Владелец ScormSession-инстанса. Пересоздаёт инстанс только при смене
// `initialSession.id` — обновления полей сессии (progress/status/score/location)
// идут внутрь через `commit()` и не трогают lifecycle инстанса.
// НЕ класть сюда зависимость от всей `initialSession`: это и ломало прошлый
// PublicPlayer — `handleSessionUpdate` апдейтил `session` → useEffect
// пересоздавал SCORM → `this.cmiData` обнулялся → progress застревал на 0.
export const ScormSessionProvider: FC<PropsWithChildren<IProviderProps>> = ({
  initialSession,
  children,
}) => {
  const instance = useMemo(
    () => new ScormSession(initialSession),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialSession.id],
  )

  useEffect(() => {
    instance.init(window)
    return () => {
      instance.dispose()
    }
  }, [instance])

  return <ScormSessionContext.Provider value={instance}>{children}</ScormSessionContext.Provider>
}

export function useScormSession(): ScormSession {
  const instance = useContext(ScormSessionContext)
  if (!instance) {
    throw new Error('useScormSession must be used inside <ScormSessionProvider>')
  }
  return instance
}

export function useScormSessionSnapshot(): IScormSessionSnapshot {
  const instance = useScormSession()
  return useSyncExternalStore(instance.subscribe, instance.getSnapshot)
}
